/** I14-00: operation metadata; the API binds the actual executor versions. */
import { RUNTIME_V3_PERIOD_GRAINS } from './common';
import type { RuntimeV3PeriodGrain } from './common';
import type { RuntimeV3AnalyticalOperation, RuntimeV3AnalyticalOperand } from './analytical-request';
import type { RuntimeV3OperatorVersionRef, RuntimeV3PlanKind } from './runs';
import type {
  RuntimeV3DatasetRevisionManifest,
  RuntimeV3MeasureAggregation,
} from './dataset-revision';
import type { RuntimeV3OrderDirection, RuntimeV3QueryPeriod } from './query-spec';
import type { RuntimeV3TraversePlanBody } from './graph-traverse';
import type { RuntimeV3ClassifyPlanBody } from './classify-against-policy';
import { isRuntimeV3DeriveOperator, RUNTIME_V3_DERIVE_OPERATOR_SPECS } from './derive-plan';
import type { RuntimeV3DeriveOperator, RuntimeV3RoleRequirement } from './derive-plan';
export type { RuntimeV3OperandRecipe, RuntimeV3RoleConstraint, RuntimeV3RoleRequirement } from './derive-plan';

export const RUNTIME_V3_OPERATION_REGISTRY_VERSION = 'runtime-v3.operation-registry.v1';
/**
 * THE ONE DECLARATION (#8148 I14-05 HOLD @ d6d67f0702, refold instruction).
 * `operation-registry.ts` has no dependency on `definitions.ts`; `definitions.ts`
 * needs only the TYPE (for `RuntimeV3MeasurePeriodDeclaration.periodBasis`) and
 * takes it as `import type` from here, so no cycle exists. `definition-
 * period-declaration.ts` takes the value from here too. No `period-basis.ts`
 * leaf file — this module is the leaf.
 */
export const RUNTIME_V3_PERIOD_BASES = ['flow', 'point_in_time', 'annualised_run_rate'] as const;
export type RuntimeV3PeriodBasis = (typeof RUNTIME_V3_PERIOD_BASES)[number];

/** Reserved vocabulary is not an executable offer until its operator is registered. */
export type RuntimeV3RegisteredOperation = Exclude<RuntimeV3AnalyticalOperation,
  'biggest_mover' | 'gap_to_target'> | 'document_lookup';

/** Adapter bodies retain their existing owner types; no second body schema. */
export type RuntimeV3OperationCompilation<
  QueryBody,
  DeriveBody,
  ScenarioBody,
  TraverseBody = RuntimeV3TraversePlanBody,
  FindingBody = RuntimeV3ClassifyPlanBody,
> =
  | Readonly<{
    operation: 'total' | 'by_member' | 'series' | 'count' | 'rank';
    body: QueryBody;
    manifest: RuntimeV3DatasetRevisionManifest;
    authorizedScopeRefs: readonly string[];
    requiredScopeRef?: string;
  }>
  | Readonly<{
    operation: 'subtract' | 'share_of_total' | 'period_over_period';
    body: DeriveBody;
  }>
  | Readonly<{ operation: 'scenario_estimate'; body: ScenarioBody }>
  | Readonly<{ operation: 'traverse'; body: TraverseBody }>
  | Readonly<{ operation: 'on_track'; body: FindingBody }>
  | Readonly<{ operation: 'document_lookup'; body: import('./document-plan').RuntimeV3DocumentPlanBody }>;

export type RuntimeV3OperationRegistryEntry = Readonly<{
  name: RuntimeV3RegisteredOperation;
  kind: 'query' | 'derivation' | 'scenario' | 'finding' | 'traverse' | 'document';
  planKind: RuntimeV3PlanKind;
  compiler: 'query' | 'derive' | 'scenario' | 'traverse' | 'finding' | 'document';
  operator: RuntimeV3OperatorVersionRef;
  signature: Readonly<{ operands: 0 | 1 | 2; output: 'scalar' | 'series' | 'set' | 'pair' | 'text' }>;
  grains: readonly RuntimeV3PeriodGrain[];
  periodBasisAccepted: readonly RuntimeV3PeriodBasis[];
  crossBasisRule: 'refuse' | 'two_labelled_bases' | 'declared_conversion_only';
  claimCost: Readonly<{ fixed: number; perMember: number; perBucket: number }>;
  /** Null means the operation is not offered by the ordinary analytical request path. */
  analyticalRequest: RuntimeV3AnalyticalRequestCapability | null;
  /**
   * Null for every operation but `rank`. Rank has no `analyticalRequest`
   * capability (a different serving path, #8385/#8386), yet its output
   * still carries one required sub-object the generic signature cannot
   * describe. A descriptor-derived probe (or any other caller building a
   * well-formed request from the registry alone) reads the required shape
   * from here instead of branching on the operation's name.
   */
  rankRequest: RuntimeV3RankRequestCapability | null;
  /**
   * PR 1's own recipe-role declaration, absent on every operation PR 1 does
   * not lower (default `[]`). An output naming `operandRoles` is validated
   * against exactly this list; an output that never names one is unaffected —
   * the field's absence changes nothing about the operations it always
   * governed through `inputOutputIds`.
   */
  requires?: readonly RuntimeV3RoleRequirement[];
}>;

type OperationDefinition = Omit<RuntimeV3OperationRegistryEntry, 'name' | 'operator'>;
export type RuntimeV3OperationDescriptor = OperationDefinition;

/** The one sub-object a well-formed `rank` output must carry; `rank.n` stays optional (#8375). */
export type RuntimeV3RankRequestCapability = Readonly<{
  direction: RuntimeV3OrderDirection;
  tiePolicy: 'label';
}>;

export type RuntimeV3AnalyticalRequestCapability = Readonly<{
  grouping: Readonly<{
    required: boolean;
    maxDimensions?: number;
    absentDefect: 'by_member_without_grouping';
    presentDefect: 'total_carries_grouping' | 'grouping_not_allowed';
    detail: string;
  }>;
  requestedPeriod: Readonly<
    | { form: 'any' }
    | { form: 'bounded'; defect: 'series_requires_explicit_period'; detail: string }
  >;
  queryPeriodForm: 'bound' | 'by_period';
  /**
   * #8212 — the aggregation the pinned measure must declare, or null for any.
   * The served compiler does not read the operation, so `count` over a summed
   * measure would publish a sum under the word "count"; binding refuses it.
   */
  measureAggregation: RuntimeV3MeasureAggregation | null;
}>;

const GRAINS = Object.freeze([...RUNTIME_V3_PERIOD_GRAINS]);
const BASES = Object.freeze([...RUNTIME_V3_PERIOD_BASES]);
const SCALAR = Object.freeze({ operands: 1, output: 'scalar' } as const);
const COST_SCALAR = Object.freeze({ fixed: 1, perMember: 0, perBucket: 0 });
const GROUPING_ABSENT = Object.freeze({
  required: false,
  absentDefect: 'by_member_without_grouping' as const,
  presentDefect: 'grouping_not_allowed' as const,
  detail: 'this operation returns one value per period and cannot also group by a dimension',
});
const TOTAL_GROUPING = Object.freeze({
  ...GROUPING_ABSENT,
  presentDefect: 'total_carries_grouping' as const,
  detail: 'a total is one figure for the whole operand; naming a grouping asks for something else, which is `by_member`',
});
const MEMBER_GROUPING = Object.freeze({
  required: true,
  absentDefect: 'by_member_without_grouping' as const,
  presentDefect: 'grouping_not_allowed' as const,
  detail: 'by_member is one figure per member, so it must name the dimension whose members those are',
});
const REQUESTED_PERIOD_ANY = Object.freeze({ form: 'any' as const });
const SERIES_REQUESTED_PERIOD = Object.freeze({
  form: 'bounded' as const,
  defect: 'series_requires_explicit_period' as const,
  detail: 'series requires an explicit range, a last_n count, or all published coverage',
});
const COUNT_GROUPING = Object.freeze({
  ...GROUPING_ABSENT,
  detail: 'a count is one figure for the whole operand and cannot also group by a dimension',
});
const TOTAL_REQUEST = Object.freeze({
  grouping: TOTAL_GROUPING, requestedPeriod: REQUESTED_PERIOD_ANY, queryPeriodForm: 'bound' as const,
  measureAggregation: null,
});
const MEMBER_REQUEST = Object.freeze({
  grouping: MEMBER_GROUPING, requestedPeriod: REQUESTED_PERIOD_ANY, queryPeriodForm: 'bound' as const,
  measureAggregation: null,
});
const SERIES_REQUEST = Object.freeze({
  grouping: { ...GROUPING_ABSENT, maxDimensions: 1, detail: 'series permits at most one dimension beside its period axis' },
  requestedPeriod: SERIES_REQUESTED_PERIOD, queryPeriodForm: 'by_period' as const,
  measureAggregation: null,
});
const COUNT_REQUEST = Object.freeze({
  grouping: COUNT_GROUPING,
  requestedPeriod: REQUESTED_PERIOD_ANY,
  queryPeriodForm: 'bound' as const,
  measureAggregation: 'count' as const,
});
const QUERY: OperationDefinition = Object.freeze({
  kind: 'query', planKind: 'query', compiler: 'query', signature: SCALAR,
  grains: GRAINS, periodBasisAccepted: BASES, crossBasisRule: 'two_labelled_bases',
  claimCost: COST_SCALAR, analyticalRequest: null, rankRequest: null,
});
const RANK_REQUEST: RuntimeV3RankRequestCapability = Object.freeze({ direction: 'desc', tiePolicy: 'label' });
const MEMBERS: OperationDefinition = Object.freeze({
  ...QUERY, signature: Object.freeze({ operands: 1, output: 'set' }),
  claimCost: Object.freeze({ fixed: 0, perMember: 1, perBucket: 0 }),
});
const DERIVE: OperationDefinition = Object.freeze({
  ...QUERY, kind: 'derivation', planKind: 'derive', compiler: 'derive',
  signature: Object.freeze({ operands: 2, output: 'scalar' }), crossBasisRule: 'refuse',
});
const TRAVERSE: OperationDefinition = Object.freeze({
  ...MEMBERS, kind: 'traverse', planKind: 'traverse', compiler: 'traverse',
  crossBasisRule: 'refuse',
});
const FINDING: OperationDefinition = Object.freeze({
  ...MEMBERS, kind: 'finding', planKind: 'finding', compiler: 'finding',
  crossBasisRule: 'refuse',
});

/**
 * #8351 PR 1 — `subtract`'s two roles, ORDERED as the operator reads them
 * (minuend minus subtrahend). `subtrahend` must share the minuend's family
 * and basis: the executor's own compatibility rule (#8100), now enforced at
 * validation rather than only inside the derive engine.
 */
const SUBTRACT_REQUIRES: readonly RuntimeV3RoleRequirement[] = Object.freeze([
  Object.freeze({
    role: 'minuend',
    recipe: Object.freeze({ kind: 'operand_total', periodFrom: 'owner' } as const),
    constraints: Object.freeze([]),
  }),
  Object.freeze({
    role: 'subtrahend',
    recipe: Object.freeze({ kind: 'operand_total', periodFrom: 'owner' } as const),
    constraints: Object.freeze([
      Object.freeze({ kind: 'same_family_as', role: 'minuend' } as const),
      Object.freeze({ kind: 'same_basis_as', role: 'minuend' } as const),
      // #8351 HOLD cure AR-01/finding 5 — same family/basis on the REQUEST
      // does not prove the same RESOLVED population/scope; checked after
      // resolution, before any leg runs (`analytical-derived-output-executor.ts`).
      Object.freeze({ kind: 'same_resolved_population_as', role: 'minuend' } as const),
    ]),
  }),
]);

// Logical operands are graph inputs, not the count of cells in an input set.
// Runtime derive admission retains its own min/max cell arity and runtime operand rules.
const DEFINITIONS: Readonly<Record<RuntimeV3RegisteredOperation, OperationDefinition>> = Object.freeze({
  total: Object.freeze({ ...QUERY, analyticalRequest: TOTAL_REQUEST }),
  by_member: Object.freeze({ ...MEMBERS, analyticalRequest: MEMBER_REQUEST }),
  series: Object.freeze({ ...QUERY,
    signature: Object.freeze({ operands: 1, output: 'series' }),
    claimCost: Object.freeze({ fixed: 0, perMember: 0, perBucket: 1 }),
    analyticalRequest: SERIES_REQUEST,
  }),
  count: Object.freeze({ ...QUERY, analyticalRequest: COUNT_REQUEST }),
  // Rank stays unserved on the `analyticalRequest` route (#8200); rankRequest
  // below is the separate #8513 serving path, consulted directly by
  // `admitSelectedOutput` (analytical-output-admission.ts, #8684) as the
  // second admission route.
  rank: Object.freeze({ ...MEMBERS, rankRequest: RANK_REQUEST }),
  subtract: Object.freeze({ ...DERIVE, requires: SUBTRACT_REQUIRES }),
  share_of_total: Object.freeze({ ...DERIVE, signature: SCALAR }),
  period_over_period: Object.freeze({ ...DERIVE,
    signature: Object.freeze({ operands: 1, output: 'pair' }),
    claimCost: Object.freeze({ fixed: 2, perMember: 0, perBucket: 0 }),
  }),
  scenario_estimate: Object.freeze({ ...QUERY, kind: 'scenario', planKind: 'scenario',
    compiler: 'scenario', signature: Object.freeze({ operands: 1, output: 'set' }),
    crossBasisRule: 'declared_conversion_only',
    claimCost: Object.freeze({ fixed: 0, perMember: 1, perBucket: 0 }),
  }),
  traverse: TRAVERSE,
  on_track: FINDING,
  document_lookup: Object.freeze({ kind: 'document', planKind: 'document', compiler: 'document',
    signature: Object.freeze({ operands: 0, output: 'text' }),
    grains: Object.freeze([]), periodBasisAccepted: Object.freeze([]),
    crossBasisRule: 'refuse', claimCost: Object.freeze({ fixed: 0, perMember: 1, perBucket: 0 }),
    analyticalRequest: null, rankRequest: null }),
});

/** The request seam reads this descriptor; absent is a typed refusal, never a default query. */
export function runtimeV3AnalyticalRequestCapability(
  operation: RuntimeV3AnalyticalOperation,
): RuntimeV3AnalyticalRequestCapability | null {
  if (!runtimeV3OperationIsRegistered(operation)) return null;
  return DEFINITIONS[operation as RuntimeV3RegisteredOperation].analyticalRequest;
}

/**
 * #8684 — the reader for rank's OWN typed descriptor, mirroring
 * `runtimeV3AnalyticalRequestCapability` exactly. Every operation but `rank`
 * returns null here, the same way `analyticalRequest` is null for every
 * operation but the ordinary query ones.
 */
export function runtimeV3RankRequestCapability(
  operation: RuntimeV3AnalyticalOperation,
): RuntimeV3RankRequestCapability | null {
  if (!runtimeV3OperationIsRegistered(operation)) return null;
  return DEFINITIONS[operation as RuntimeV3RegisteredOperation].rankRequest;
}

/** Registered but without a request capability is `operation_not_served`, not unknown (#8385). */
export function runtimeV3OperationIsRegistered(operation: unknown): boolean {
  return typeof operation === 'string'
    && Object.prototype.hasOwnProperty.call(DEFINITIONS, operation);
}

/**
 * Whether a served operation may bind a pinned measure declaring this
 * aggregation. #8684 — rank has no `analyticalRequest` capability (#8200),
 * but its `rankRequest` descriptor declares no aggregation constraint
 * either: an operation the registry admits through the OTHER typed route
 * defers here exactly like an unconstrained `analyticalRequest` operation
 * would, rather than reading the absent capability as a universal refusal.
 */
export function runtimeV3AnalyticalRequestAdmitsMeasure(
  operation: RuntimeV3AnalyticalOperation,
  aggregation: RuntimeV3MeasureAggregation | undefined,
): boolean {
  const capability = runtimeV3AnalyticalRequestCapability(operation);
  if (capability === null) return runtimeV3RankRequestCapability(operation) !== null;
  // Unconstrained operations keep their prior binding, even over a measure the revision lacks.
  return capability.measureAggregation === null || capability.measureAggregation === aggregation;
}

/**
 * The query-period form one executable request operation requires. #8684 —
 * rank's admitted output binds the requested window UNCHANGED, the same
 * "bound" resolution total/by_member/count already get; only a `null` here
 * throws at output binding (`analytical-output-binding.ts`'s
 * `periodDecision`), so rank's second, already-typed descriptor is
 * consulted before that unconditional refusal would be reached.
 */
export function runtimeV3AnalyticalRequestQueryPeriod(
  operation: RuntimeV3AnalyticalOperation,
  period: RuntimeV3QueryPeriod,
): RuntimeV3QueryPeriod | null {
  const capability = runtimeV3AnalyticalRequestCapability(operation);
  if (capability === null) return runtimeV3RankRequestCapability(operation) !== null ? period : null;
  return capability.queryPeriodForm === 'by_period'
    ? { ...period, form: 'by_period' }
    : period;
}

/**
 * The declaration an interpreter may use to validate an operation's structural
 * inputs before an API composition root supplies its operator version.
 *
 * This deliberately returns the existing row rather than a second allowlist:
 * registration, arity, compiler and cross-basis policy stay one declaration.
 */
export function runtimeV3OperationDescriptor(
  name: string,
): RuntimeV3OperationDescriptor | null {
  return DEFINITIONS[name as RuntimeV3RegisteredOperation] ?? null;
}

/** A registered derive shape that the interpreted scalar-output path can lower today. */
export type RuntimeV3InterpretedScalarDeriveOperation = Readonly<{
  operation: RuntimeV3DeriveOperator;
  descriptor: RuntimeV3OperationDescriptor;
}>;

/**
 * One descriptor-backed admission for interpreted scalar derivations. The
 * derive engine's existing required-input flags keep unsupported operations
 * from appearing executable before this path carries those typed inputs.
 */
export function runtimeV3InterpretedScalarDeriveOperation(
  name: string,
): RuntimeV3InterpretedScalarDeriveOperation | null {
  const descriptor = runtimeV3OperationDescriptor(name);
  if (descriptor?.compiler !== 'derive' || descriptor.signature.output !== 'scalar'
    || descriptor.crossBasisRule !== 'refuse' || !isRuntimeV3DeriveOperator(name)) return null;
  const engine = RUNTIME_V3_DERIVE_OPERATOR_SPECS[name];
  if (engine.requiresConstant || engine.requiresLag || engine.requiresSeries
    || engine.runtimeOperand !== 'none') return null;
  return { operation: name, descriptor };
}

/** Version references come from executor constants, never copied into this package. */
export function createRuntimeV3OperationRegistry(
  operators: Readonly<Record<'query' | 'derive' | 'scenario' | 'traverse' | 'finding', RuntimeV3OperatorVersionRef>>
    & Readonly<{ document?: RuntimeV3OperatorVersionRef }>,
): readonly RuntimeV3OperationRegistryEntry[] {
  return Object.freeze((Object.keys(DEFINITIONS) as RuntimeV3RegisteredOperation[])
    .filter((name) => operators[DEFINITIONS[name].compiler] !== undefined).map((name) => {
      const definition = DEFINITIONS[name];
      return Object.freeze({ ...definition, name,
        operator: Object.freeze({ ...operators[definition.compiler]! }),
      });
    }));
}

/** Accounting basis remains distinct from time basis; no inference from units or labels. */
export function runtimeV3PeriodBasisCorresponds(
  providerBasis: NonNullable<RuntimeV3AnalyticalOperand['basis']>['value'],
  periodBasis: RuntimeV3PeriodBasis | undefined,
): boolean {
  if (periodBasis === undefined || !BASES.includes(periodBasis)) return false;
  return (providerBasis === 'run_rate') === (periodBasis === 'annualised_run_rate');
}
